import { CollectionMethod } from "./billing-engine-types";
import { BillingPeriod } from "./service-invoice";

/** Sunday-indexed, matching `Date.getDay()`. */
export const MIN_DAY_OF_WEEK = 0;
export const MAX_DAY_OF_WEEK = 6;

/**
 * Capped at 28 so every month has the day — the same bound the billing
 * engine's `targetBillingDay` uses for the same reason.
 */
export const MIN_DAY_OF_MONTH = 1;
export const MAX_DAY_OF_MONTH = 28;

export const MAX_NET_DAYS = 365;

/**
 * Weekly anchors on a day of the week; every other cadence anchors on a day of
 * the month. Yearly additionally repeats in the month its cycle started, the
 * same rule `calculateNextInvoicePeriod` applies to yearly subscriptions.
 */
export function cadenceAnchorsOnDayOfMonth(cadence: BillingPeriod): boolean {
  return cadence !== BillingPeriod.WEEKLY;
}

export interface NetTermsSettings {
  /**
   * Whether this merchant is invoiced on terms instead of the prepaid wallet.
   * OMS-specific: it selects the billing model, unlike the two fields below,
   * which describe how the resulting invoice is charged and dated and mean the
   * same here as they do on subscription billing.
   */
  enabled: boolean;
  /** Reuses the billing engine's period so the two agree on what a cycle is. */
  cadence: BillingPeriod;
  /** The day to invoice on when the cadence is weekly. */
  dayOfWeek?: number | null;
  /** The day to anchor on for every other cadence. */
  dayOfMonth?: number | null;
  /** Charge the payment method when due, or email and let the merchant pay. */
  collectionMethod: CollectionMethod;
  /** Days from the invoice date to the due date — the "net" in net 7. */
  netTermsDays: number;
}

/**
 * Cadence and net period are independent by design: weekly/net 7 and
 * monthly/net 60 are both valid. This only rejects a combination that cannot
 * be scheduled, and returns a message meant to be shown to the operator.
 */
export function validateNetTermsSettings(
  settings: NetTermsSettings,
): string | undefined {
  if (!settings.enabled) {
    return undefined;
  }

  if (!Number.isInteger(settings.netTermsDays) || settings.netTermsDays < 1) {
    return "Net period must be a whole number of days, at least 1.";
  }
  if (settings.netTermsDays > MAX_NET_DAYS) {
    return `Net period cannot exceed ${MAX_NET_DAYS} days.`;
  }

  if (cadenceAnchorsOnDayOfMonth(settings.cadence)) {
    return isWithin(settings.dayOfMonth, MIN_DAY_OF_MONTH, MAX_DAY_OF_MONTH)
      ? undefined
      : `This cadence needs a day of the month between ${MIN_DAY_OF_MONTH} and ${MAX_DAY_OF_MONTH}.`;
  }

  return isWithin(settings.dayOfWeek, MIN_DAY_OF_WEEK, MAX_DAY_OF_WEEK)
    ? undefined
    : "Weekly billing needs a day of the week.";
}

function isWithin(
  value: number | null | undefined,
  min: number,
  max: number,
): boolean {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= min &&
    value <= max
  );
}
